import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import type { ExternalIdentifier } from 'types/writer'

import { updateWriter } from '@disco/state-management/actions/rights-data-actions'
import { useGetRightsHolderByIdQuery } from '@disco/state-management/api/rights-holders'
import { updateSplit } from '@disco/state-management/reducers/rightsManagement'

import { useDispatch } from 'hooks/use-redux'

import { BaseDialog } from '../../components/base-dialog'
import {
  useDuplicateWriterSearch,
  useRightsNewModelEnabled,
  useUpdateRightsEntity,
} from '../../hooks'
import type { RightsDataModalLocationState } from '../../types'

interface Writer {
  id: number
  name: string
  external_identifiers?: ExternalIdentifier[]
  [key: string]: any
}

/**
 * EditWriterModal - Modal for editing an existing writer
 *
 * This modal allows users to edit a writer's name, IPI and ISNI identifier.
 * The writer ID is extracted from the route params.
 *
 * When edited from a split row (via splitId and splitsKey in location state),
 * the split is automatically updated with the edited writer to ensure it
 * remains selected in the dropdown.
 *
 * Includes duplicate name detection via debounced search to warn users
 * if a writer with the same name already exists in their contact list.
 */
export function EditWriterModal() {
  const dispatch = useDispatch()
  const location = useLocation()
  const { writerId } = useParams<{ writerId: string }>()
  const locationState = location.state as RightsDataModalLocationState | null
  const { splitId, splitsKey } = locationState || {}

  const rightsNewModelEnabled = useRightsNewModelEnabled()

  // New API: fetch by ID from route param
  const { data: rightsHolderData } = useGetRightsHolderByIdQuery(
    writerId ? parseInt(writerId, 10) : 0,
    { skip: !rightsNewModelEnabled || !writerId },
  )

  // Use new API data or fall back to location state
  const writer: Writer | undefined = useMemo(() => {
    if (rightsNewModelEnabled) {
      return rightsHolderData
        ? {
            id: rightsHolderData.id,
            name: rightsHolderData.title,
            external_identifiers: rightsHolderData.external_identifiers,
          }
        : undefined
    }
    // Old API: get writer from location state (passed by old dropdown)
    return (locationState as { writer?: Writer } | null)?.writer
  }, [rightsNewModelEnabled, rightsHolderData, locationState])

  const [inputValue, setInputValue] = useState(writer?.name || '')

  // Sync inputValue when writer changes (e.g., async load)
  useEffect(() => {
    if (writer?.name) {
      setInputValue(writer.name)
    }
  }, [writer?.name])

  const { warningMessage } = useDuplicateWriterSearch({
    inputValue,
    excludeId: writer?.id,
  })

  const handleNameChange = useCallback((value: string) => {
    setInputValue(value)
  }, [])

  const updateEntity = useUpdateRightsEntity({
    entityType: 'Writer',
    updateThunk: updateWriter,
  })

  const handleSave = useCallback(
    async (
      name: string,
      externalIdentifiers: ExternalIdentifier[] | undefined,
    ) => {
      if (!writer) return

      await updateEntity(
        writer.id,
        name,
        externalIdentifiers,
        writer.external_identifiers,
      )

      // Auto-select the edited writer in the split that triggered the edit
      if (splitId && splitsKey) {
        dispatch(
          updateSplit({
            trackId: splitsKey,
            splitId,
            changes: { writerId: writer.id },
          }),
        )
      }
    },
    [writer, updateEntity, dispatch, splitId, splitsKey],
  )

  // Don't render if writer data not found
  if (!writer) {
    return null
  }

  return (
    <BaseDialog
      heading="Edit writer"
      nameFieldLabel="Writer name"
      initialName={writer.name}
      initialIdentifiers={writer.external_identifiers}
      helperText="Editing this contact will update it everywhere it's used."
      onSave={handleSave}
      isEditMode={true}
      warningMessage={warningMessage}
      onNameChange={handleNameChange}
      showIPI
      showISNI
      open
    />
  )
}

export default EditWriterModal
